import { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, CircularProgress, Box,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Speed as PingIcon } from '@mui/icons-material';
import type { NodeResponse } from '../api/types';
import { PROTOCOL_LABELS } from '../utils/format';

interface Props {
  nodes: NodeResponse[];
  isLoading: boolean;
  onEdit: (node: NodeResponse) => void;
  onDelete: (node: NodeResponse) => void;
}

export function NodeTable({ nodes, isLoading, onEdit, onDelete }: Props) {
  const [pings, setPings] = useState<Record<string, number | null>>({});
  const [pinging, setPinging] = useState<Record<string, boolean>>({});

  const handlePing = async (node: NodeResponse) => {
    setPinging(prev => ({ ...prev, [node.id]: true }));
    try {
      const res = await fetch(`/api/v1/nodes/${node.id}/ping`, { method: 'POST' });
      const data = await res.json();
      setPings(prev => ({ ...prev, [node.id]: data.data?.latency ?? null }));
    } catch {
      setPings(prev => ({ ...prev, [node.id]: null }));
    }
    setPinging(prev => ({ ...prev, [node.id]: false }));
  };

  const latencyColor = (ms: number) => {
    if (ms < 100) return 'success.main';
    if (ms < 300) return 'warning.main';
    return 'error.main';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        暂无节点，点击"添加节点"或"导入节点"开始
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>节点名称</TableCell>
            <TableCell>协议</TableCell>
            <TableCell>地址</TableCell>
            <TableCell>地区</TableCell>
            <TableCell>延迟</TableCell>
            <TableCell>状态</TableCell>
            <TableCell align="right">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map(node => (
            <TableRow key={node.id} hover>
              <TableCell>{node.name}</TableCell>
              <TableCell>
                <Chip label={PROTOCOL_LABELS[node.protocol] || node.protocol} size="small" variant="outlined" />
              </TableCell>
              <TableCell>{node.address}:{node.port}</TableCell>
              <TableCell>{node.region || '—'}</TableCell>
              <TableCell>
                {pings[node.id] !== undefined ? (
                  pings[node.id] !== null ? (
                    <Chip
                      label={`${pings[node.id]}ms`}
                      size="small"
                      sx={{ color: 'white', bgcolor: latencyColor(pings[node.id]!), fontWeight: 600 }}
                    />
                  ) : (
                    <Chip label="超时" size="small" color="error" variant="outlined" />
                  )
                ) : (
                  <IconButton size="small" onClick={() => handlePing(node)} disabled={pinging[node.id]}>
                    {pinging[node.id] ? <CircularProgress size={16} /> : <PingIcon fontSize="small" />}
                  </IconButton>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={node.isActive ? '启用' : '禁用'}
                  size="small"
                  color={node.isActive ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handlePing(node)} title="测速"><PingIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => onEdit(node)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => onDelete(node)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
